<template>
  <form class="url-box" :class="{ 'url-box--on-dark': onDark }" novalidate @submit.prevent="$emit('submit')">
    <label :for="inputId" class="visually-hidden">Address of the page with your form</label>
    <div class="url-box-field" :class="{ 'url-box-field--error': error }">
      <ph-icon name="link-simple" :size="20" class="url-box-icon" />
      <input
        :id="inputId"
        ref="input"
        :value="value"
        class="url-box-input"
        type="url"
        inputmode="url"
        autocomplete="off"
        autocapitalize="none"
        spellcheck="false"
        placeholder="https://yourwebsite.com/contact"
        :readonly="loading"
        :aria-invalid="error ? 'true' : 'false'"
        :aria-describedby="`${inputId}-message`"
        @input="$emit('input', $event.target.value.trim())"
      />
      <button type="submit" class="ex-btn ex-btn--primary url-box-button" :disabled="loading" :aria-busy="loading ? 'true' : 'false'">
        <template v-if="loading">
          <ph-icon name="spinner-gap" :size="18" class="url-box-spinner" />
          Scoring
        </template>
        <template v-else>
          <ph-icon name="arrow-right" :size="18" />
          <span class="ex-btn__label" data-label="Score my form">Score my form</span>
        </template>
      </button>
    </div>
    <p :id="`${inputId}-message`" class="url-box-message" :class="{ 'url-box-message--error': error }" aria-live="polite">
      {{ error }}
    </p>
  </form>
</template>

<script>
import PhIcon from './PhIcon.vue'

export default {
  name: 'UrlBox',
  components: { PhIcon },
  props: {
    value: { type: String, default: '' },
    inputId: { type: String, required: true },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    onDark: { type: Boolean, default: false }
  },
  methods: {
    focus () {
      this.$refs.input.focus({ preventScroll: true })
    }
  }
}
</script>

<style scoped>
.url-box {
  width: 680px;
  max-width: 100%;
}

.url-box-field {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 64px;
  padding: 0 8px 0 22px;
  border: 1px solid transparent;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-white);
}

.url-box-field:focus-within {
  box-shadow: var(--ex-focus-ring);
  border-color: var(--ex-color-blue);
}

.url-box-field--error {
  border-color: var(--ex-color-error-foreground);
}

.url-box-icon {
  color: var(--ex-color-neutral-300);
}

.url-box-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  background: transparent;
  color: var(--ex-color-dark);
  font-family: var(--ex-font-body);
  font-size: 17px;
}

.url-box-input::placeholder {
  color: var(--ex-color-neutral-300);
  opacity: 1;
}

.url-box-input:focus {
  outline: none;
  box-shadow: none;
}

.url-box-button:disabled {
  background: var(--ex-color-blue);
  border-color: var(--ex-color-blue);
  color: var(--ex-color-white);
  cursor: progress;
}

.url-box-message {
  min-height: 20px;
  margin-top: 10px;
  font-size: 14px;
  text-align: left;
}

.url-box-message--error {
  color: var(--ex-color-error-foreground);
}

.url-box--on-dark .url-box-message {
  color: var(--ex-color-neutral-100);
}

.url-box--on-dark .url-box-message--error {
  color: var(--ex-color-error-surface);
}

@media (prefers-reduced-motion: no-preference) {
  .url-box-spinner {
    animation: url-box-spin 1s linear infinite;
  }
}

@keyframes url-box-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .url-box-field {
    flex-wrap: wrap;
    height: auto;
    padding: 0;
    border-radius: 0;
    background: transparent;
    gap: 8px;
  }

  .url-box-field:focus-within {
    box-shadow: none;
  }

  .url-box-icon {
    display: none;
  }

  .url-box-input {
    flex-basis: 100%;
    height: 56px;
    padding: 0 20px;
    border-radius: var(--ex-radius-pill);
    background: var(--ex-color-white);
  }

  .url-box-input:focus {
    box-shadow: var(--ex-focus-ring);
  }

  .url-box-button {
    width: 100%;
  }
}
</style>
